import React, { useRef, useEffect, useState } from 'react';
import { string, arrayOf, func, shape, bool, number, oneOf } from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import LazyLoad from 'vanilla-lazyload/dist/lazyload.min.js';
import { GalleryImage, MODE } from './components';
import './Gallery.scss';

const getClassNames = (index, lazyloadClassName) => {
    let classNames;

    if (index > 0) {
        classNames = { lazyload: lazyloadClassName };
    }

    return classNames;
};

export default function Gallery({
    className,
    classNames,
    mainImage,
    thumbnails,
    mode,
    cdn,
    lazyload,
    activeThumbnailIndex,
    onChange,
}) {
    const galleryElement = useRef(null);
    const rootClassnames = classnames('gallery-root', className, {
        'gallery-root--visual': mode === MODE.VISUAL,
    });

    useEffect(() => {
        if (galleryElement.current) {
            galleryElement.current.scroll(
                activeThumbnailIndex * galleryElement.current.offsetWidth,
                0,
            );
        }
    }, [activeThumbnailIndex, galleryElement.current]);

    const [lazylazyInstances, setLazylazyInstances] = useState([]);
    useEffect(() => {
        const galleryLazyload = new LazyLoad({
            elements_selector: '.gallery-root',
            callback_enter: el => {
                lazylazyInstances.push(
                    new LazyLoad({
                        container: el,
                        threshold: -10,
                    }),
                );
            },
        });
        setLazylazyInstances(lazylazyInstances);

        return () => {
            galleryLazyload.destroy();
            lazylazyInstances.forEach(lazyloadInstance =>
                lazyloadInstance.destroy(),
            );
        };
    }, []);

    const handleScroll = debounce(evt => {
        if (onChange) {
            onChange(
                evt,
                Math.round(
                    galleryElement.current.scrollLeft /
                        galleryElement.current.offsetWidth,
                ),
            );
        }
    }, 300);

    return (
        <div
            className={rootClassnames}
            ref={galleryElement}
            onScroll={handleScroll}
        >
            {thumbnails.length > 0 ? (
                thumbnails.map((thumbnail, index) => (
                    <GalleryImage
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${thumbnail}${index}`}
                        className="gallery-root__image"
                        classNames={getClassNames(
                            index,
                            classNames.galleryLazyload,
                        )}
                        src={`${cdn.CDN}${thumbnail}`}
                        placeholder={`${cdn.CDN_IMAGE}/placeholder.svg`}
                        lazyload={index === 0 ? lazyload : true}
                        mode={mode}
                        cdn={cdn}
                    />
                ))
            ) : (
                <GalleryImage
                    className="gallery-root__image"
                    src={mainImage}
                    placeholder={`${cdn.CDN_IMAGE}/placeholder.svg`}
                    lazyload={lazyload}
                    cdn={cdn}
                    mode={mode}
                />
            )}
        </div>
    );
}

Gallery.propTypes = {
    className: string,
    classNames: shape({
        galleryLazyload: string,
    }),
    mainImage: string.isRequired,
    thumbnails: arrayOf(string).isRequired,
    activeThumbnailIndex: number,
    cdn: shape({ CDN: string, CDN_IMAGE: string }).isRequired,
    mode: oneOf([MODE.DEFAULT, MODE.VISUAL]),
    lazyload: bool,
    onChange: func,
};

Gallery.defaultProps = {
    className: null,
    classNames: {},
    activeThumbnailIndex: 0,
    lazyload: false,
    onChange: null,
    mode: MODE.DEFAULT,
};
